import React, { useMemo, useState } from 'react';
import {
  ActionButton,
  Callout,
  DirectionalHint,
  Icon,
  SearchBox,
  Stack,
  Text,
  mergeStyleSets,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';

/** Eén adres dat gekozen kan worden. */
export interface EmailOption {
  email: string;
  /** Naam van de persoon, of de leverancier bij een algemeen adres. */
  name?: string | null;
  /** Extra duiding, bv. de functie van de contactpersoon. */
  hint?: string | null;
}

/** Adressen gebundeld per herkomst, zodat de lijst hiërarchie heeft. */
export interface EmailGroup {
  label: string;
  options: EmailOption[];
}

interface Props {
  groups: EmailGroup[];
  /** Adressen die al gekozen zijn; die worden als toegevoegd getoond. */
  selected: string[];
  onPick: (email: string) => void;
  disabled?: boolean;
  buttonText?: string;
}

const styles = mergeStyleSets({
  callout: {
    width: 380,
    maxHeight: 420,
    overflowY: 'auto',
    padding: '12px 0',
  },
  groep: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#605e5c',
    padding: '10px 16px 4px 16px',
  },
  optie: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    padding: '7px 16px',
    cursor: 'pointer',
    selectors: { ':hover': { background: '#f3f2f1' } },
  },
  optieGekozen: { cursor: 'default', selectors: { ':hover': { background: 'transparent' } } },
  naam: { fontSize: 13, fontWeight: 600, color: '#241C16' },
  adres: { fontSize: 12, color: '#605e5c' },
  rol: { fontSize: 11, color: '#a19f9d' },
  leeg: { padding: '12px 16px', fontSize: 12, color: '#605e5c' },
  zoek: { padding: '0 12px 4px 12px' },
});

/**
 * Laat de gebruiker een e-mailadres kiezen dat al in het systeem staat.
 *
 * Adressen worden nu vaak met de hand ingetypt terwijl ze bij de leverancier of
 * zijn contactpersonen bekend zijn — met tikfouten en verkeerde ontvangers tot
 * gevolg. Deze kiezer toont ze gegroepeerd per herkomst.
 */
const EmailPicker: React.FC<Props> = ({
  groups,
  selected,
  onPick,
  disabled = false,
  buttonText = 'Kies uit contacten',
}) => {
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState('');
  const buttonId = useId('email-picker');

  const gekozen = useMemo(
    () => new Set(selected.map(e => e.toLowerCase())),
    [selected],
  );

  const zichtbaar = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    return groups
      .map(groep => ({
        ...groep,
        options: groep.options.filter(
          o => o.email
            && (!term
              || o.email.toLowerCase().includes(term)
              || String(o.name || '').toLowerCase().includes(term)),
        ),
      }))
      .filter(groep => groep.options.length > 0);
  }, [groups, zoek]);

  const totaal = groups.reduce((n, g) => n + g.options.filter(o => o.email).length, 0);

  return (
    <>
      <ActionButton
        id={buttonId}
        iconProps={{ iconName: 'ContactList' }}
        onClick={() => setOpen(!open)}
        disabled={disabled || totaal === 0}
        title={totaal === 0 ? 'Geen bekende adressen' : undefined}
      >
        {buttonText}
      </ActionButton>

      {open && (
        <Callout
          className={styles.callout}
          target={`#${buttonId}`}
          onDismiss={() => {
            setOpen(false);
            setZoek('');
          }}
          directionalHint={DirectionalHint.bottomRightEdge}
          setInitialFocus
        >
          {totaal > 6 && (
            <div className={styles.zoek}>
              <SearchBox
                placeholder='Zoeken'
                value={zoek}
                onChange={(_, v) => setZoek(v || '')}
                onClear={() => setZoek('')}
                underlined
              />
            </div>
          )}

          {zichtbaar.length === 0 && (
            <div className={styles.leeg}>Geen adressen gevonden.</div>
          )}

          {zichtbaar.map(groep => (
            <div key={groep.label}>
              <div className={styles.groep}>{groep.label}</div>
              {groep.options.map(optie => {
                const alGekozen = gekozen.has(optie.email.toLowerCase());
                return (
                  <button
                    type='button'
                    key={`${groep.label}-${optie.email}`}
                    className={`${styles.optie} ${alGekozen ? styles.optieGekozen : ''}`}
                    disabled={alGekozen}
                    onClick={() => onPick(optie.email)}
                  >
                    <Icon
                      iconName={alGekozen ? 'CheckMark' : 'Mail'}
                      styles={{
                        root: {
                          fontSize: 13,
                          color: alGekozen ? '#71BF43' : '#605e5c',
                          flexShrink: 0,
                        },
                      }}
                    />
                    <Stack styles={{ root: { minWidth: 0 } }}>
                      {optie.name && <Text className={styles.naam}>{optie.name}</Text>}
                      <Text className={styles.adres}>{optie.email}</Text>
                      {optie.hint && <Text className={styles.rol}>{optie.hint}</Text>}
                    </Stack>
                  </button>
                );
              })}
            </div>
          ))}
        </Callout>
      )}
    </>
  );
};

export default EmailPicker;
